import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart, useAuth } from '../App';
import apiClient from '../lib/api';

/**
 * MobileBottomNav
 *
 * A fixed, app-shell-style bottom tab bar rendered exclusively on mobile
 * viewports (below the `lg` / 1024px breakpoint). Hidden on desktop.
 *
 * Tabs: Home · Search · Cart (badge) · Messages (unread badge) · Account
 *
 * T20 enhancements:
 *  - Active state: filled SVG icon variant + accent color (not just thicker stroke)
 *  - Cart badge: red circle with item count from CartContext
 *  - Tap feedback: scale-down to 0.95 on press, spring back to 1 on release
 *    (CSS active:scale-95 on the Link + transition-transform)
 *  - Safe-area padding: inline paddingBottom with env(safe-area-inset-bottom)
 *
 * Suppressed on admin (/admin/*), seller (/seller/*), and vendor (/vendor/*)
 * dashboard routes where the full-page navigation takes over.
 */

// ---------------------------------------------------------------------------
// Filled + outline SVG icon pairs
// Using inline SVG so we can swap between filled (active) and outline (inactive)
// without importing a separate icon library that may not have filled variants.
// All icons are 22×22 and match the Lucide visual style (rounded caps/joins).
// ---------------------------------------------------------------------------

/** Home icon — filled when active, outline when inactive */
function HomeIcon({ filled }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={filled ? 0 : 1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {filled ? (
        <path d="M10.707 2.293a1 1 0 0 1 1.414 0l8 8A1 1 0 0 1 20 12h-1v9a1 1 0 0 1-1 1h-5v-5h-2v5H6a1 1 0 0 1-1-1v-9H4a1 1 0 0 1-.707-1.707l8-8z" />
      ) : (
        <>
          <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </>
      )}
    </svg>
  );
}

/** Search icon — filled (bold) when active, outline when inactive */
function SearchIcon({ filled }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={filled ? 2.5 : 1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

/** ShoppingCart icon — filled when active, outline when inactive */
function CartIcon({ filled }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth={filled ? 0 : 1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {filled ? (
        <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4zM3 6h18M16 10a4 4 0 0 1-8 0" />
      ) : (
        <>
          <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <path d="M16 10a4 4 0 0 1-8 0" />
        </>
      )}
    </svg>
  );
}

/** MessageSquare icon — filled when active, outline when inactive */
function MessageIcon({ filled }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth={filled ? 0 : 1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {filled ? (
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      ) : (
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      )}
    </svg>
  );
}

/** User icon — filled when active, outline when inactive */
function AccountIcon({ filled }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? 'none' : 'currentColor'}
      strokeWidth={filled ? 0 : 1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {filled ? (
        <>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </>
      ) : (
        <>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </>
      )}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// MobileBottomNav
// ---------------------------------------------------------------------------
const MobileBottomNav = () => {
  const location = useLocation();
  const { cart } = useCart();
  const { user } = useAuth();

  // Unread message count — polled every 30 s when the user is logged in,
  // mirroring the same logic used in Header.jsx so both badges stay in sync.
  const [unreadCount, setUnreadCount] = useState(0);
  const mountedRef = useRef(true);

  const fetchUnread = useCallback(() => {
    if (!user || !mountedRef.current) return;
    apiClient
      .get('/messages/unread-count')
      .then((res) => {
        if (mountedRef.current) {
          setUnreadCount(res.data?.unread_count ?? 0);
        }
      })
      .catch(() => {
        // Non-critical — fail silently
      });
  }, [user]);

  useEffect(() => {
    mountedRef.current = true;
    if (!user) {
      setUnreadCount(0);
      return;
    }
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, [user, fetchUnread]);

  // Suppress on dashboard routes that have their own navigation
  const path = location.pathname;
  const isHiddenRoute =
    path.startsWith('/admin') ||
    path.startsWith('/seller') ||
    path.startsWith('/vendor');

  if (isHiddenRoute) return null;

  // Cart item count — prefer item_count from server shape, fall back to
  // array length for robustness.
  const cartCount =
    typeof cart?.item_count === 'number'
      ? cart.item_count
      : (cart?.items?.length ?? 0);

  /**
   * Determine the "active" state for a tab.
   *
   * The home tab uses exact matching to avoid `/search` or `/cart` looking
   * active when we're on `/`. All other tabs use prefix matching so nested
   * paths (e.g. `/dashboard/messages`) light up the right tab.
   */
  const isActive = (tabPath, exact = false) => {
    if (exact) return path === tabPath;
    // `/dashboard` prefix — must not match `/dashboard/messages` for the
    // account tab when the messages tab also starts with `/dashboard`.
    if (tabPath === '/dashboard') {
      return path === '/dashboard' || (path.startsWith('/dashboard') && !path.startsWith('/dashboard/messages'));
    }
    return path === tabPath || path.startsWith(tabPath + '/');
  };

  const tabs = [
    {
      id: 'home',
      path: '/',
      label: 'Home',
      IconComponent: HomeIcon,
      exact: true,
      badge: null,
    },
    {
      id: 'search',
      path: '/search',
      label: 'Search',
      IconComponent: SearchIcon,
      exact: false,
      badge: null,
    },
    {
      id: 'cart',
      path: '/cart',
      label: 'Cart',
      IconComponent: CartIcon,
      exact: false,
      badge: cartCount > 0 ? cartCount : null,
    },
    {
      id: 'messages',
      // Messages is a nested route inside the customer dashboard
      path: '/customer/messages',
      label: 'Messages',
      IconComponent: MessageIcon,
      exact: false,
      // Only show the badge when the user is logged in and has unread messages
      badge: user && unreadCount > 0 ? unreadCount : null,
    },
    {
      id: 'account',
      path: '/dashboard',
      label: 'Account',
      IconComponent: AccountIcon,
      exact: false,
      badge: null,
    },
  ];

  return (
    /*
     * The outer element is visually hidden on large screens via `lg:hidden`.
     * On small/medium screens it is fixed to the bottom edge and sits above
     * all page content at z-50.
     *
     * The inline `paddingBottom` is intentional: Tailwind's `pb-*` classes
     * cannot express `env(safe-area-inset-bottom)` without a custom plugin,
     * and we already have the `.safe-area-pb` utility in index.css.
     * Using the class directly on the nav ensures the clickable area is always
     * above the home indicator on iOS / PWA installs.
     */
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-50
                 bg-background border-t border-border
                 safe-area-pb"
      aria-label="Mobile navigation"
      role="navigation"
    >
      {/* Active tab indicator — a thin accent-colored line that slides
          horizontally to sit above the active tab icon. Implemented as a
          CSS transition on `left` so it animates smoothly between tabs
          without JavaScript animation libraries.                          */}
      <div
        className="relative flex items-stretch"
        style={{ height: '64px' }}
      >
        {tabs.map((tab) => {
          const active = isActive(tab.path, tab.exact);
          const { IconComponent } = tab;

          return (
            <Link
              key={tab.id}
              to={tab.path}
              aria-label={tab.label}
              aria-current={active ? 'page' : undefined}
              className={[
                // Full-height flex column so the touch target fills the bar
                // T20: touch target is the full 64px height of the nav bar
                'relative flex flex-col items-center justify-center flex-1',
                'min-h-[44px] select-none',
                // T20: tap scale feedback — the whole Link element scales on press
                // `active:scale-95` + `transition-transform` gives a spring-like feel
                'transition-all duration-150 active:scale-95',
                // Color transitions — icon + label shift from muted to accent
                active
                  ? 'text-[#FC0000]'
                  : 'text-muted-foreground hover:text-foreground',
              ].join(' ')}
            >
              {/* Active indicator bar — 2 px line that slides in from bottom */}
              <span
                aria-hidden="true"
                className={[
                  'absolute top-0 left-[20%] right-[20%] h-[2px] rounded-b-full',
                  'transition-all duration-200',
                  active
                    ? 'bg-[#FC0000] opacity-100'
                    : 'bg-transparent opacity-0',
                ].join(' ')}
              />

              {/* T20: Icon with filled variant on active state */}
              <span className="relative mt-1">
                <IconComponent filled={active} />
                {tab.badge !== null && (
                  <span
                    aria-label={`${tab.badge} unread`}
                    className={[
                      'absolute -top-1.5 -right-1.5',
                      'min-w-[16px] h-[16px] px-[3px]',
                      'bg-[#FC0000] text-white',
                      'text-[9px] font-bold leading-none',
                      'rounded-full flex items-center justify-center',
                      'ring-1 ring-background',
                      // Scale-in animation when the badge first appears
                      'animate-in zoom-in-75 duration-150',
                    ].join(' ')}
                  >
                    {tab.badge > 99 ? '99+' : tab.badge}
                  </span>
                )}
              </span>

              {/* Label */}
              <span
                className={[
                  'text-[10px] mt-0.5 leading-none font-medium',
                  'transition-all duration-150',
                  active ? 'font-semibold' : '',
                ].join(' ')}
              >
                {tab.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileBottomNav;
